import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, FolderKanban, Users, MessageSquare } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { StatusBadge, PriorityBadge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import { projectService } from '@/services';
import { fmtDate } from '@/utils/format';

export default function Projects() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['projects'], queryFn: () => projectService.list({ limit: 100 }) });

  const openChat = (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/projects/${projectId}/chat`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        subtitle="Anyone can create a project and add teammates"
        actions={
          <Link to="/projects/new" className="btn-primary">
            <Plus size={16} /> New project
          </Link>
        }
      />

      {isLoading ? (
        <Loader />
      ) : (data?.items || []).length === 0 ? (
        <EmptyState
          title="No projects yet"
          subtitle="Create your first project to get started"
          action={
            <Link to="/projects/new" className="btn-primary">
              <Plus size={16} /> New project
            </Link>
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((p) => (
            <div
              key={p._id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/projects/${p._id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/projects/${p._id}`);
                }
              }}
            >
              <Card className="h-full hover:shadow-soft transition cursor-pointer relative">
                <button
                  type="button"
                  onClick={(e) => openChat(e, p._id)}
                  title="Open chat"
                  className="absolute top-3 right-3 p-2 rounded-lg text-slate-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-500/10"
                  aria-label="Open project chat"
                >
                  <MessageSquare size={16} />
                </button>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 grid place-items-center">
                    <FolderKanban size={18} />
                  </div>
                  <div className="flex gap-1 mr-8">
                    <StatusBadge status={p.status} />
                    <PriorityBadge priority={p.priority} />
                  </div>
                </div>
                <div className="font-semibold">{p.name}</div>
                {p.code && <div className="text-xs text-slate-400">{p.code}</div>}
                <div className="text-sm text-slate-500 mt-2 line-clamp-2">{p.description || 'No description'}</div>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Users size={12} /> {(p.members || []).length} members
                  </span>
                  {p.endDate && <span>Due {fmtDate(p.endDate)}</span>}
                </div>
                <div className="mt-3">
                  <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-brand-500 to-indigo-500" style={{ width: `${p.progress || 0}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{p.progress || 0}% complete</div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
